import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'

import type { IContext } from './columns'
import type { FiltersState } from '../Filters'
import type { ReactElement } from 'react'
import type { IReviewTopic } from '~/types/review'

import columns from './columns'

import ManageTopicModal from '../../Modals/ManageTopicModal'
import ReviewsManagementFilters, { applicationFilterFn } from '../Filters'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { useDeleteReviewTopicMutation, useReviewsTopicsQuery, keys as reviewTopicsKeys } from '~/queries/reviews-topics'
import { resize } from '~/utils/media'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ENTRIES_NAME = 'review-topics'

const TopicsManagement = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const tableRef = useRef<HTMLDivElement>(null)

  const [selectedTopic, setSelectedTopic] = useState<IReviewTopic>()

  const [isManageModalOpen, setIsManageModalOpen] = useState(false)

  const [isDeleteModalOpen, setDeleteModalState] = useState(false)

  const [filters, setFilters] = useState<FiltersState>()

  const reviewsWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.REVIEWS.WRITE])

  /** Queries & Mutations */

  const { data: apps } = useAppsManagementListQuery()

  const { data: topics } = useReviewsTopicsQuery()

  const { mutateAsync: deleteTopic } = useDeleteReviewTopicMutation()

  /** Effects */

  useDocumentTitle('Reviews | Topics management')

  /** Handlers */

  const handleFiltersChange = (newFilters: FiltersState) => setFilters(newFilters)

  const handleManageModalState = (isOpen: boolean, topic?: IReviewTopic) => () => {
    setSelectedTopic(topic)
    setIsManageModalOpen(isOpen)
  }

  const handleToggleDeleteModal = (isOpen: boolean, topic?: IReviewTopic) => () => {
    setSelectedTopic(topic)
    setDeleteModalState(isOpen)
  }

  const handleDeleteTopic = async () => {
    if (selectedTopic) {
      try {
        await deleteTopic({ topicId: selectedTopic.id })
        await queryClient.invalidateQueries({ queryKey: reviewTopicsKeys.list() })
        displayToast('Topic deleted', 'success')
      } catch (e) {
        displayToast('Topic could not be deleted', 'error')
        captureException(e)
      } finally {
        setDeleteModalState(false)
      }
    }
  }

  const handleManageTopicSuccess = () => {
    setIsManageModalOpen(false)
    displayToast(`Topic ${selectedTopic ? 'edited' : 'created'}`, 'success')
  }

  const handleManageTopicFailure = (errorMessage: string) => {
    setIsManageModalOpen(false)
    displayToast(errorMessage, 'error')
  }

  const rowData = useMemo(
    () =>
      topics?.filter(applicationFilterFn(filters)).map((topic) => ({
        ...topic,
        applications:
          topic.applications?.map((app) => {
            const application = apps?.find(({ app_key }) => app_key === app.app_key)
            return {
              ...app,
              app_icon_url: application?.icon_file_id
                ? resize(`${FASTLY_PATH}${application.icon_file_id}`, 32, 32)
                : undefined,
            }
          }) || [],
      })),
    [apps, filters, topics]
  )

  const context = useMemo(
    (): IContext => ({
      handleDelete: handleToggleDeleteModal,
      handleEdit: handleManageModalState,
      reviewsWriteAuthorized: reviewsWriteAuthorized,
    }),
    [reviewsWriteAuthorized]
  )

  /** Render */

  return (
    <>
      <ViewTitle className="layout-view-title top-zero">Topics management</ViewTitle>
      <ReviewsManagementFilters onFiltersChange={handleFiltersChange} />
      <Layout className="reviews-topics-management-view" withPadding>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!reviewsWriteAuthorized}
            addButtonLabel="New topic"
            addButtonTooltip={!reviewsWriteAuthorized ? 'unauthorized' : undefined}
            className="topics-table with-min-height"
            columnDefs={columns}
            context={context}
            entriesName={ENTRIES_NAME}
            onAddButtonClick={handleManageModalState(true)}
            rowData={rowData}
            rowStyle={{ cursor: 'default' }}
            suppressScrollOnNewData
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      <ManageTopicModal
        isOpen={isManageModalOpen}
        onClose={handleManageModalState(false)}
        onFailure={handleManageTopicFailure}
        onSuccess={handleManageTopicSuccess}
        topic={selectedTopic}
      />
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Do you really want to delete this topic?"
        onClose={handleToggleDeleteModal(false)}
        onConfirm={handleDeleteTopic}
      />
    </>
  )
}

export default TopicsManagement
